import { AxiosError } from 'axios';
import { AppointmentType } from 'router/subrouters/ListingCreationWizard/subrouters/UnderExternalAdministration/default-router/pages/BusinessInfo/utils';
import { DropzoneFile } from 'components/Dropzone/utils';
import { User } from './User';
import { ObjectValues, XOR } from 'types';
import { WorkflowConfigurationSteps } from './WorkflowConfiguration';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import { YesNo } from './Fields';
import {
  AskingPriceSelect,
  DealRoom,
  Currency,
  StockAtValue,
  DealRoomLocation,
} from './DealRoom';
import { DataRoom } from 'router/subrouters/DataRoom/models/DataRoom';
import { Industry } from './Industry';

export type CreditCard = {
  brand: string;
  last4: string;
  expMonth: string;
  expYear: string;
};

export type PaymentInfo = {
  stripeId: string;
  type: string;
  card: CreditCard;
};

export type ControlledInformation = {
  ABN: number;
  ACN: number;
  postcode: number;
};

export const LISTING_TYPE = {
  GOING_CONCERN: 'Profitable going concern',
  URGENT: 'Company/assets in Administration',
} as const;

export type ListingType = ObjectValues<typeof LISTING_TYPE>;

export const LISTING_GOING_CONCERN = LISTING_TYPE.GOING_CONCERN;
export const LISTING_URGENT = LISTING_TYPE.URGENT;

export type FetchListingError = AxiosError<{
  error: Record<string, any>;
  message: string;
}>;

export const getShowSensitiveContent = (listing: Listing) => {
  if (!listing || STATUSES_TO_HIDE.includes(listing.stage)) return false;

  return true;
};

export type ListingEnquiry = {
  _id: string;
  listingId: string;
  sellerId: string;
  seller?: User;
  listing?: Listing;
  numberOfEnquiredLeads?: number;
  numberOfAcceptedLeads?: number;
  numberOfConfirmedLeads?: number;
  numberOfOfferedLeads?: number;
  progressedStep?: number;
  workflowConfiguration?: WorkflowConfigurationSteps;
  leadIds?: string[];
};

export const LISTING_TIERS = {
  STANDARD: 'standard',
  PREMIUM: 'premium',
  PLATINUM: 'platinum',
} as const;

export type ListingTier = ObjectValues<typeof LISTING_TIERS>;

export const LISTING_STAGE = {
  NO_LISTING: 'no_listing',
  DRAFT: 'draft',
  PUBLIC: 'public',
  PRIVATE: 'private',
  PAUSED: 'paused',
  ENDED: 'ended',
} as const;

export type ListingStage = ObjectValues<typeof LISTING_STAGE>;

export const STATUSES_TO_ENQUIRE: ListingStage[] = [
  LISTING_STAGE.PUBLIC,
  LISTING_STAGE.PRIVATE,
];
export const STATUSES_TO_WATCH: ListingStage[] = [
  LISTING_STAGE.PUBLIC,
  LISTING_STAGE.PRIVATE,
];
export const STATUSES_TO_HIDE: ListingStage[] = [
  LISTING_STAGE.ENDED,
  LISTING_STAGE.PAUSED,
];

export type Listing = {
  _id: string;

  // status
  stage: ListingStage;

  // NDA
  envelopeTemplateId?: string;
  envelopeTemplateName?: string;
  envelopeTemplateUploaded?: Date;

  //listing package subscription
  hasActiveListingPackage: boolean;
  hasActiveMediaPackage: boolean;
  isApproved: boolean;
  paymentIntentId?: string;
  isExtendableListingPackage: boolean;
  paymentClientSecret: string | null;
  activeListingInvoiceId: string;

  //business page slug name logic
  slugName: string;
  fullSlugName: string;
  lastActiveSlugName: string;
  slugNameHistory: string[];

  //LCW props
  //Business info step
  type: ListingType;
  location?: MapValue;
  locationState?: LocationState;
  dealLocation: DealRoomLocation;
  industry: Industry;
  listingTitle: string;
  businessDescription: string;
  shortDescription: string;
  highlights: string[];
  products: string[];
  keyAssets: string[];
  paymentInfo: PaymentInfo;
  profitMeasure: string;
  priceGuide: string;
  appointmentType: AppointmentType;
  otherAppointmentType: string;
  locationPostcode: string;
  deadline: Array<ListingDeadline>;
  seekingExpressionOfInterestDate: string;

  //Listing contact step - moved from LCW to deal
  listingContact: ListingContactType;
  listingContacts: ListingContactType[]; //virtual property

  //Statement of information
  askingPriceSelect: AskingPriceSelect;
  amount: number | null;
  amountFrom: number | null;
  amountTo: number | null;
  revenue: number;
  COGS: number | null;
  grossProfit: number | null;
  lessOverheads: number | null;
  operatingProfit: number | null;
  depreciationAndAmortisation: number | null;
  EBITDA: number;
  EBIT: number | null;
  EBITDAMultiple?: number;
  adjustments: ListingAdjustments[];
  totalAdjustments: number | null;
  priceDescription: PriceDescription;
  accountingPeriod: string;
  enableTeaser?: boolean;
  teaserVerification: TeaserVerification;
  teaserType: TeaserType;
  teaserFile?: DropzoneFile;
  embeddedTeaserVideoFile?: DropzoneFile;
  includeVideoInTeaser: IncludeVideoInTeaser;
  embeddedVideoCode: string;
  averageStockValue?: number;
  stockAtValue: StockAtValue;
  ownerOfTheBusiness?: OwnerOfTheBusiness;
  someoneToReplaceOwner?: boolean | YesNo;
  hasCommercialProperty?: boolean;
  commercialProperty?: CommercialProperty;
  saleType: SaleType;
  contractedCustomers?: boolean | YesNo;
  revenueModel: RevenueModel[];
  controlledInformation: ControlledInformation;
  succession?: Succession;

  listingPackage: ListingPackage;
  mediaPackage: MediaPackage;
  buyerOutreach: BuyerOutreach;

  //Additional props
  gallery: DropzoneFile[] | null;

  expirationDate: Date;
  publishedOn: Date;
  viewsCounter: number;
  timeSpentCounter: number;
  enquiriesCounter: number;
  watchlistCounter: number;
  tier: ListingTier;
  watchlistUsers: string[];
  isCreatedBySuperAdmin: boolean;
  dataRoomDocumentsAvailabilityAfterSignNDA: DocumentInfoType[];
  useExternalListingContactInformation: boolean;
  bypassedPaymentStep: boolean;
  purchaseFreeholdCommercialProperty: boolean;
  currency: Currency;

  //VIRTUAL PROPERTIES
  missingControlledInformation: boolean;
  tierMapped: string;
  commercialPropertyOption: string;
  marketingPackageMapped: string;
  isInactive: boolean;
  isPrivateViewer: boolean;
  isWatchlisted: boolean;
  isEnquired: boolean;
  isWithdrawn: boolean;
  isOwner: boolean;
  isCoOwner: boolean;
  // Currently available data room nodes (non-folder) for user that made a request
  dataRoomNodes: DataRoomNode[];
  //property to say if user can message broker from /businesses page
  messageBroker: boolean;
  //chat session id return if message broker set on true
  sessionId: string;
  //for spreadsheet stats
  brokerName: string;
  companyName: string;
  additionalBrokerName: string;
  additionalCompanyName: string;
  leadsInfo: string;
  watchersInfo: string;
  numberOfEnquiredLeads: number;
  numberOfAcceptedLeads: number;
  numberOfConfirmedLeads: number;
  numberOfOfferedLeads: number;
  dataRoomName: string;

  //Mongo metadata
  createdAt: string;
  updatedAt: string;

  //RELATIONS
  userId: string;
  user: Partial<User> | null;

  adminId: string;
  admin: Partial<User> | null;

  listingEnquiryId: string;
  listingEnquiry: Partial<ListingEnquiry> | null;

  dataRoomId: string;
  dataRoom: Partial<DataRoom> | null;

  dealRoomId: string;
  dealRoom: Partial<DealRoom> | null;

  pendingBrokerId: string;
  privateViewers: string[];

  taxInvoice: TaxInvoice;

  //ENTERPISE
  isEnterprise?: boolean;

  // Skip payment props
  skipPayment: boolean;

  isContextRequired: boolean;

  // LCW steps
  stepsCompleted: number;
  currentStep?: number;
} & BossModeSkipPayment &
  ListingRequiredVisibleInfo;

export type BossModeSkipPayment = {
  clientFirstNameForGeneratingInvoice: string;
  clientLastNameForGeneratingInvoice: string;
  clientEmailForGeneratingInvoice?: string;
  clientCompanyForGeneratingInvoice: string;
  paymentDueDate: string;
  paymentDueDateDescription: string;
  transferListingToClient?: boolean;
  reasonForNotGeneratingInvoice?: string;
};

export type TaxInvoice = {
  name: string;
  legalEntity: string;
  ABN?: string;
  NZBN?: string;
  address: string;
};

export type ListingContactType = {
  firstName: string;
  lastName: string;
  name?: string;
  email: string;
  phoneNumber: string;
  linkedinProfile: string;
  position?: string;
  companyId?: string;
  company?: Company;
  companyName?: string;
  firmLogo?: DropzoneFile;
  brokerImage?: DropzoneFile;
};

export type ListingDeadline = {
  noFixedDate?: boolean;
  timeline: string;
  timelineDate?: string;
  timelineTime?: string;
};

type Company = {
  _id: string;
  ACN: string;
  ABN: string;
  name: string;
  firmLogo?: DropzoneFile;
};

export const LOCATION_STATE = {
  QLD: 'QLD',
  NSW: 'NSW',
  TAS: 'TAS',
  VIC: 'VIC',
  WA: 'WA',
  SA: 'SA',
  NT: 'NT',
  ACT: 'ACT',
  NNZ: 'NNZ',
  SNZ: 'SNZ',
  INTERNATIONAL: 'International',
} as const;

export const NZ_STATE = 'NZ';

export type MapValue = {
  state: LocationState;
  longitude: number;
  latitude: number;
};

export type LocationState = typeof LOCATION_STATE[keyof typeof LOCATION_STATE];

export type StatementOfInformationProps = Pick<
  Listing,
  'industry' | 'locationPostcode'
> & {
  amount: string;
  revenue: string;
  EBITDA: string;
  location: string;
  ownersAlsoOwnCommercialProperty?: string;
};

export const LISTING_PACKAGE = {
  // Profitable listings
  BRONZE: 'Bronze', // Standard listing tier - initially
  SILVER: 'Silver', // Premium listing tier - initially
  GOLD: 'Gold', // Platinum listing tier - initially

  // Urgent listings
  BASIC: 'Basic', // Premium listing tier - initially
  COMPREHENSIVE: 'Comprehensive', // Platinum listing tier - initially

  // Profitable listings + Urgent listings
  NO_PACKAGE: 'no_package', // Standard listing tier - initially
} as const;

export type ListingPackage = ObjectValues<typeof LISTING_PACKAGE>;

export const MEDIA_PACKAGE = {
  TRIED_AND_TESTED: 'Tried and tested',
  TARGETED: 'Targeted',
  PRINT_PACKAGE: 'Print package',
  THE_WORKS: 'The works',
} as const;
export type MediaPackage = ObjectValues<typeof MEDIA_PACKAGE>;

export const BUYER_OUTREACH = {
  TWO_WEEKS: 'Two weeks',
  FOUR_WEEKS: 'Four weeks',
} as const;
export type BuyerOutreach = ObjectValues<typeof BUYER_OUTREACH>;

export type TrendingListing = Partial<Listing>;

export type DocumentInfoType = {
  fileId: string;
  name: string;
  checked: boolean;
};

export const PRICE_DESCRIPTION = {
  ACTUAL: 'actual',
  FORECAST: 'forecast',
} as const;

export type PriceDescription = ObjectValues<typeof PRICE_DESCRIPTION>;

export type ListingAdjustments = {
  name: string;
  grossAmount: number;
  addBack: number;
  netAmount: number;
};

export const OWNER_OF_THE_BUSINESS = {
  SILENT: 'silent',
  WORK_IN_THE_BUSINESS: 'work_in_the_business',
} as const;

export type OwnerOfTheBusiness = ObjectValues<typeof OWNER_OF_THE_BUSINESS>;

export const COMMERCIAL_PROPERTY = {
  SELLING: 'selling',
  NOT_SELLING: 'not_selling',
  WILL_OFFER: 'will_offer',
} as const;

export type CommercialProperty = ObjectValues<typeof COMMERCIAL_PROPERTY>;

export const SALE_TYPE = {
  SHARE: 'share',
  ASSET: 'asset',
  EITHER: 'either',
} as const;

export type SaleType = ObjectValues<typeof SALE_TYPE>;

export const REVENUE_MODEL = {
  AD_HOC: 'ad_hoc',
  RECURRING: 'recurring',
  COMMISSION: 'commission',
} as const;

export type RevenueModel = ObjectValues<typeof REVENUE_MODEL>;

export const TEASER_VERIFICATION = {
  WITHOUT_EMAIL_VERIFICATION: 'without_email_verification',
  WITH_EMAIL_VERIFICATION: 'with_email_verification',
} as const;

export type TeaserVerification = ObjectValues<typeof TEASER_VERIFICATION>;

export const TEASER_TYPE = {
  RESOLVE_DIGITAL_TEASER: 'resolve_digital_teaser',
  OWN_TEASER_FILE: 'own_teaser_file',
} as const;

export type TeaserType = ObjectValues<typeof TEASER_TYPE>;

export const PAYMENT_OPTION = {
  CREDIT_CARD: 'credit_card',
  INVOICE: 'invoice',
} as const;

export type PaymentOption = ObjectValues<typeof PAYMENT_OPTION>;

export const SUCCESSION = {
  GM_IN_PLACE: 'gm_in_place',
  GM_REQUIRED: 'gm_required',
  SUCCESSOR_IN_PLACE: 'successor_in_place',
} as const;

export type Succession = ObjectValues<typeof SUCCESSION>;

export type AskingPrice = XOR<AskingPriceAmount, AskingPriceFromTo>;

type AskingPriceAmount = {
  amount: number;
};

export type AskingPriceFromTo = {
  amountFrom: number;
  amountTo: number;
};

export const INCLUDE_VIDEO_IN_TEASER = {
  NO: 'no',
  WITH_EMBEDDED_CODE: 'with_embedded_code',
  WITH_VIDEO_FILE: 'with_video_file',
} as const;

export type IncludeVideoInTeaser = ObjectValues<typeof INCLUDE_VIDEO_IN_TEASER>;

export const REASON_TO_HIDE = {
  HIDDEN_BY_BROKER: 'hidden_by_broker',
  AVAILABLE_UNDER_NDA: 'available_under_nda',
  CUSTOM: 'custom',
} as const;

export type ReasonToHide = ObjectValues<typeof REASON_TO_HIDE>;

export const REVEAL_AFTER = {
  NEVER: 'never',
  AFTER_ENQUIRE: 'after_enquire',
  AFTER_NDA_SIGNED: 'after_nda_signed',
} as const;

export type RevealAfter = ObjectValues<typeof REVEAL_AFTER>;

export type RequiredVisibleInfo = {
  isRequired: boolean;
  isVisible: boolean;

  reasonToHide?: ReasonToHide;
  customReasonToHide?: string;
  revealAfter?: RevealAfter;
};

export type ListingRequiredVisibleInfo = {
  amountFromRequiredVisibleInfo?: RequiredVisibleInfo;
  amountToRequiredVisibleInfo?: RequiredVisibleInfo;
  amountRequiredVisibleInfo?: RequiredVisibleInfo;
  COGSRequiredVisibleInfo?: RequiredVisibleInfo;
  lessOverheadsRequiredVisibleInfo?: RequiredVisibleInfo;
  depreciationAndAmortisationRequiredVisibleInfo?: RequiredVisibleInfo;
  EBITRequiredVisibleInfo?: RequiredVisibleInfo;
  accountingPeriodRequiredVisibleInfo?: RequiredVisibleInfo;
  successionRequiredVisibleInfo?: RequiredVisibleInfo;
  hasCommercialPropertyRequiredVisibleInfo?: RequiredVisibleInfo;
  revenueModelRequiredVisibleInfo?: RequiredVisibleInfo;
} & CommonListingRequiredVisibleInfo;

export type CommonListingRequiredVisibleInfo = {
  revenueRequiredVisibleInfo?: RequiredVisibleInfo;
  EBITDARequiredVisibleInfo?: RequiredVisibleInfo;
  averageStockValueRequiredVisibleInfo?: RequiredVisibleInfo;
};
